import React, {  useLayoutEffect } from 'react';
import "tabs-pattern/dist/foundation.css";
import { tabs as tabsInit } from 'tabs-pattern/src/tabs-ds2';
import type { TabData } from '../../config/tabsConfig';
import { trackCustomPageView } from '../../services/analytics';

// Tab analytics configuration
const TAB_ANALYTICS_CONFIG: { [key: string]: string | ((href: string) => string | null) } = {
  'My Documents': 'my-documents-tab',
  'Application': 'details-tab',
  'Employment information': 'manager-portal:view-details:employment',
  'School information': 'manager-portal:view-details:school',
  'Class information': 'manager-portal:view-details:class',
  'Applicant information': 'manager-portal:view-details:applicant',
  'Other applications': 'manager-portal:view-details:other-apps',
  'Past applications': 'past-apps',
  'Pending approval applications': 'manager-portal',
  'Employee information': (href: string) => 
    href.includes('/tuition/overview') ? 'my-employee-info:employee-info-tab' : null,
  'Employee balance': 'my-employee-info:employee-balance-tab',
};

const getTabAnalyticsPageView = (tabTitle: string, currentHref: string = globalThis.location?.href || ''): string | null => {
  const config = TAB_ANALYTICS_CONFIG[tabTitle];
  if (!config) return null;
  if (typeof config === 'function') return config(currentHref);
  return config;
};
 
// ✅ Declare pljs on Window type
declare global {
  interface Window {
    pljs: {
      getCurrentLang: (el?: HTMLElement) => string;
      createElementFromHTML?: (html: string) => HTMLElement;
      createInnerWrap?: (el: HTMLElement, tag: string, attr: string, value: string) => void;
      getWindowWidth?: () => number;
      getElCount?: (els: NodeListOf<Element>) => number;
    };
  }
}
 
interface TabsProps {
  tabs: TabData[];
  extraProps?: Record<string, any>;
  activeTab?: number;
  setActiveTab?: (id: number) => void;
}
 
const Tabs: React.FC<TabsProps> = ({ tabs: tabsList, extraProps, activeTab, setActiveTab }) => {
 
 
  // If activeTab/setActiveTab are not provided, use local state for fallback
  const [internalActiveTab, internalSetActiveTab] = React.useState(tabsList[0]?.id);
  const currentTab = typeof activeTab === 'number' ? activeTab : internalActiveTab;
  const changeTab = setActiveTab || internalSetActiveTab;
  
  useLayoutEffect(() => {
    // Small delay to ensure DOM is fully ready
    const timer = setTimeout(() => {
      try {
        tabsInit();
        console.log(' Tabs initialized successfully');
      } catch (error) {
        console.error(' Tabs init error:', error);
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [tabsList.length]);
  return (
    <div
      className={`ds-tabs -overflow-button grid-container !pl-0`}
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
      data-pl-variation="overflow-button"
      data-pl-version="2"
      data-panel-announcement-start=""
      data-panel-announcement-end=""
    >
      <div className="ds-tabs__nav-container">
        <div className="ds-tabs__nav">
          <ul className="ds-tabs__list">
            {tabsList.map((tab) => (
              <li key={tab.id} className="ds-tabs__list-item">
                <button
                  className='ds-tabs__link'
                  onClick={(e) => {
                    e.preventDefault();
                    changeTab(tab.id);
                    // Track tab selection as custom page view (URL doesn't change)
                    console.log(`📊 Analytics: Tab selected - ${tab.title}`);
                    const pageView = getTabAnalyticsPageView(tab.title);
                    if (pageView) {
                      trackCustomPageView(pageView);
                    }
                  }}
                >
                  <span className="ds-tabs__text">{tab.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="ds-tabs__nav-more-button">
          <button className="more-button__trigger" aria-haspopup="true" tabIndex={0}>
            <span className="screenreader-only">show all</span>
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <use href="assets/icons/ds2/morehorizontal.svg#more-horizontal"></use>
            </svg>
          </button>
          <ul className="more-button__list"></ul>
        </div>
      </div>
      <div className="ds-tabs__content">
        {(() => {
          const activeTabData = tabsList.find(tab => tab.id === currentTab);
          if (!activeTabData) return null;
          const Component = activeTabData.component;
          return (
            <div key={activeTabData.id} className="ds-tabs__panel is-active">
              <Component {...(extraProps || {})} />
            </div>
          );
        })()}
      </div>
    </div>
  );
};
 
export default Tabs;
 